import { useMemo, type ReactElement } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import type { HelpCenterCategory } from '@/lib/api/help-center.server';
import {
    findKeywordMatches,
    parseSearchKeywords,
    stripHtml,
} from '@/components/editorial-faq-help-search/filter-articles';
import { cn } from '@/lib/utils';

export interface HelpCenterCategorySectionsProps {
    category: HelpCenterCategory;
    query?: string;
    className?: string;
    noSectionNames?: boolean;
}

/**
 * Renders FAQ title/body with optional keyword marks. Zendesk article markup is rendered when the
 * body has no highlighted match; highlighting uses plain-text offsets so HTML tags are never split.
 */
function HighlightText({ text, query, html = false }: { text: string; query: string; html?: boolean }): ReactElement {
    const plainText = useMemo(() => (html ? stripHtml(text) : text), [html, text]);
    const matches = useMemo(() => findKeywordMatches(plainText, parseSearchKeywords(query)), [plainText, query]);

    if (!matches.length) {
        if (html) {
            return (
                <div
                    className="editorial-rich-text body-copy-l"
                    // eslint-disable-next-line react/no-danger -- Help Center HTML is sanitized during import.
                    dangerouslySetInnerHTML={{ __html: text }}
                />
            );
        }
        return <span>{text}</span>;
    }

    const nodes: ReactElement[] = [];
    let cursor = 0;
    for (const match of matches) {
        if (match.start > cursor) {
            nodes.push(<span key={`text-${cursor}`}>{plainText.slice(cursor, match.start)}</span>);
        }
        nodes.push(
            <mark key={`match-${match.start}`} className="rounded bg-brown-90 text-neutral-10">
                {plainText.slice(match.start, match.end)}
            </mark>
        );
        cursor = match.end;
    }
    if (cursor < plainText.length) {
        nodes.push(<span key={`text-${cursor}`}>{plainText.slice(cursor)}</span>);
    }

    return <span>{nodes}</span>;
}

export default function HelpCenterCategorySections({
    category,
    query = '',
    className,
    noSectionNames = false,
}: HelpCenterCategorySectionsProps): ReactElement {
    return (
        <div
            className={cn('flex flex-col gap-10', noSectionNames && 'gap-0', className)}
            data-testid="help-center-category-sections">
            {category.sections.map((section) => (
                <section key={section.id} className="flex flex-col gap-4">
                    {!noSectionNames && <h3 className="title-m text-neutral-10">{section.name}</h3>}
                    <Accordion type="single" collapsible>
                        {section.articles.map((article) => (
                            <AccordionItem
                                key={article.id}
                                value={String(article.id)}
                                className="border-b border-neutral-80">
                                <AccordionTrigger className="body-copy-l-bold text-left">
                                    <HighlightText text={article.title} query={query} />
                                </AccordionTrigger>
                                <AccordionContent className="text-neutral-20">
                                    <HighlightText text={article.body} query={query} html />
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </section>
            ))}
        </div>
    );
}
